import { updateExpiresTime, umuStorageHelper, storageExpiresKey } from './umuStorage';
export { umuStorageHelper };

/**
 * local storage keys
 */
export enum STORAGE_KEY {
    /**
     * 设置已读的用户成就弹窗
     */
    UN_READ_DIALOG_REQUEST = 'un_read_dialog_request',

    /**
     * 设置获取课程学习证书是否已经弹窗的标记
     */
    READ_DIALOG_COURSE_CERTIFICATE = 'read_dialog_course_certificate',
    /**
     * 设置获取学习项目学习证书是否已经弹窗的标记
     */
    READ_DIALOG_PROGRAM_CERTIFICATE = 'read_dialog_program_certificate',

    /**
     * 保存区号
     */
    AREA_CODE = 'areaCode',

    /**
     * 登录用户ID
     */
    LOGIN_USER_ID = 'LOGIN_USER_ID',

    /**
     * 语音微课播放进度
     */
    AUDIO_PLAY_PROGRESS = 'AUDIO_PLAY_PROGRESS',

    /**
     * 提问讨论匿名时保存的临时用户名
     */
    POST_NAME = 'UName',

    /**
     * 直播编辑页面的引导提示浮层是否已读
     */
    LIVE_EDIT_USAGE_GUIDE_READED = 'LIVE_EDIT_USAGE_GUIDE_READED',

    /**
     * 发言框发送内容
     */
    COMMENT_POST_VALUE = 'COMMENT_POST_VALUE',

    /**
     * 发言框最后一次发送时间
     */
    COMMENT_POST_LAST_TIME = 'COMMENT_POST_LAST_TIME',

    /**
     * 判断用户是否收藏
     */
    SET_IS_COLLECT_STATE = 'SET_IS_COLLECT_STATE',

    /**
     * 是否第一次加入考题本
     */
    IS_FIRST_QUESTION_COLLECTION = 'IS_FIRST_QUESTION_COLLECTION',

    /**
     * 成员是否第一次进入学习圈
     */
    IS_MEMBER_FIRST_LEARNING_CRICLE = 'IS_MEMBER_FIRST_LEARNING_CRICLE',

    /**
     * 成员是否第一次进入学习圈
     */
    LEARNING_CIRCLE_DRAFT_TOPIC = 'LEARNING_CIRCLE_DRAFT_TOPIC',

    ACTIVITY_ANNUAL_2019_BGM_PLAYING = 'ACTIVITY_ANNUAL_2019_BGM_PLAYING',

    ACTIVITY_ANNUAL_2020_SHARE_BG = 'ACTIVITY_ANNUAL_2020_SHARE_BG__',

    ACTIVITY_ANNUAL_2020_THEME_TEXT = 'ACTIVITY_ANNUAL_2020_THEME_TEXT__',

    /** 2021年度报告 */
    ACTIVITY_ANNUAL_2021_BGM_PLAYING = 'ACTIVITY_ANNUAL_2021_BGM_PLAYING',
    ACTIVITY_ANNUAL_2021_SHARE_BG = 'ACTIVITY_ANNUAL_2021_SHARE_BG_',

    /**
     * 存储小节学习时长
     */
    CONTENT_SESSION_LEARNING_TIME = 'CONTENT_SESSION_LEARNING_TIME',

    /**
     * 进入USE练习首页的来源
     */
    USE_PRACTICE_FROM_SOURCE = 'USE_PRACTICE_FROM_SOURCE',
}

export enum STORAGE_VALUE {
    TRUE = '1',
    FALSE = '0',
}

const isSupportLocalStorage = (() => {
    try {
        const isSupport = 'localStorage' in window && window.localStorage !== null;
        if (isSupport) {
            // safari隐身模式里边也不能用
            localStorage.setItem('__test', '1');
            localStorage.removeItem('__test');
        }
        return isSupport;
    } catch (e) {
        return false;
    }
})();

const cleanupExpiredStorage = async (cacheKeys: string[] = []) => {
    if (!isSupportLocalStorage) {
        return;
    }

    let localExpireRecord: Record<string, number> | null = null;
    try {
        localExpireRecord = await umuStorageHelper.get(storageExpiresKey);
    } catch (error) {
        localExpireRecord = null;
    }

    if (!localExpireRecord || typeof localExpireRecord !== 'object') {
        return;
    }

    try {
        if (cacheKeys.length) {
            for (const key of cacheKeys) {
                delete localExpireRecord[key];
            }
        } else {
            const now = Date.now();
            for (const key in localExpireRecord) {
                if (localExpireRecord[key] < now) {
                    localStorage.removeItem(key);
                    delete localExpireRecord[key];
                }
            }
        }

        umuStorageHelper.set(storageExpiresKey, localExpireRecord);
    } catch (error) {
        // ignore
    }
};

/**
 * data persistent in browser，localStorage with cookie fallback
 * usage:
 * storageHelper.set('foo','bar')
 * storageHelper.get('foo') //bar
 * http://stackoverflow.com/a/12302790/1553656
 */
export const localStorageHelper = {
    support: isSupportLocalStorage,
    set(name: STORAGE_KEY | string, value: string, cacheDays = 30, onError?: (error: Error) => void) {
        if (this.support) {
            try {
                localStorage.setItem(name, value);
                cacheDays && updateExpiresTime(name, cacheDays);
            } catch (e) {
                onError?.(e as Error);
            }
        } else {
            cookieHelper.set(name, value, cacheDays);
        }
    },
    get(name: STORAGE_KEY | string, onError?: (error: Error) => void) {
        try {
            if (this.support) {
                return localStorage.getItem(name);
            } else {
                return cookieHelper.get(name);
            }
        } catch (e) {
            onError?.(e as Error);
            return null;
        }
    },
    del(name: STORAGE_KEY | string) {
        if (this.support) {
            localStorage.removeItem(name);
            cleanupExpiredStorage([name]);
        } else {
            this.set(name, '', -1);
        }
    },
};

/**
 * 功能和local storage类似,
 * 移动端兼容效果较好
 * 隐身模式下和local storage一样
 */
export const sessionStorageHelper = {
    support: (() => {
        try {
            const isSupport = 'sessionStorage' in window && window.sessionStorage !== null;
            if (isSupport) {
                // safari隐身模式里边也不能用
                sessionStorage.setItem('__test', '1');
                sessionStorage.removeItem('__test');
            }
            return isSupport;
        } catch (e) {
            return false;
        }
    })(),
    set(name: STORAGE_KEY | string, value: string, cacheDays: number = 1, onError?: (error: Error) => void) {
        if (this.support) {
            try {
                sessionStorage.setItem(name, value);
            } catch (e) {
                onError?.(e as Error);
            }
        } else {
            cookieHelper.set(name, value, cacheDays);
        }
    },
    get(name: STORAGE_KEY | string, onError?: (error: Error) => void) {
        try {
            if (this.support) {
                return sessionStorage.getItem(name);
            } else {
                return cookieHelper.get(name);
            }
        } catch (e) {
            onError?.(e as Error);
            return null;
        }
    },
    del(name: STORAGE_KEY | string) {
        if (this.support) {
            sessionStorage.removeItem(name);
        } else {
            this.set(name, '', -1);
        }
    },
};

/**
 * 获取和设置cookie值
 */
export const cookieHelper = {
    set(name: STORAGE_KEY | string, value: string, cacheDays: number = 1) {
        let expires = '';
        if (cacheDays) {
            const date = new Date();
            date.setTime(date.getTime() + cacheDays * 24 * 60 * 60 * 1000);
            expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + value + expires + '; path=/';
    },
    get(name: STORAGE_KEY | string) {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1, c.length);
            }
            if (c.indexOf(nameEQ) === 0) {
                return c.substring(nameEQ.length, c.length);
            }
        }
        return null;
    },
    del(name: STORAGE_KEY | string) {
        this.set(name, '', -1);
    },
};

if (typeof window !== 'undefined') {
    const cleanupCallback = () => {
        if (document.hidden || document.visibilityState === 'hidden') {
            cleanupExpiredStorage();
            window.removeEventListener('visibilitychange', cleanupCallback);
        }
    };
    window.addEventListener('visibilitychange', cleanupCallback);
}
